import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Minus, ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/motion/Reveal";
import { SERVICES } from "@/data/site";
import { openWhatsApp } from "@/lib/wa";

const SPANS = {
  "sac-kuafor": "lg:col-span-7",
  "gelin-turban": "lg:col-span-5",
  "cilt-guzellik": "lg:col-span-4",
  epilasyon: "lg:col-span-4",
  "kirpik-tirnak": "lg:col-span-4",
};

const HEIGHTS = {
  "sac-kuafor": "h-[440px] lg:h-[520px]",
  "gelin-turban": "h-[440px] lg:h-[520px]",
  "cilt-guzellik": "h-[400px]",
  epilasyon: "h-[400px]",
  "kirpik-tirnak": "h-[400px]",
};

function ServiceCard({ s }) {
  const [open, setOpen] = useState(false);
  return (
    <FadeUp className={SPANS[s.slug]}>
      <article
        className={`group relative ${HEIGHTS[s.slug]} img-zoom rounded-2xl overflow-hidden bg-espresso`}
        data-testid={`service-card-${s.slug}`}
      >
        <img
          src={s.image}
          alt={s.alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/20 via-30% to-transparent to-68%" />

        <div className="relative z-10 flex h-full flex-col justify-between p-6 sm:p-8 text-ivory [text-shadow:_0_2px_18px_rgba(20,15,12,0.7)]">
          <div className="flex items-start justify-between">
            <span className="font-serif text-5xl text-gold/90 leading-none">{s.index}</span>
            <button
              onClick={() => setOpen((o) => !o)}
              aria-label="Detayları Gör"
              data-testid={`service-toggle-${s.slug}`}
              className="rounded-full border border-ivory/30 p-2 transition-colors hover:bg-ivory hover:text-espresso"
            >
              {open ? <Minus size={18} /> : <Plus size={18} />}
            </button>
          </div>

          <div>
            <h3 className="font-serif text-3xl sm:text-4xl mb-2">{s.title}</h3>

            <AnimatePresence initial={false}>
              {open ? (
                <motion.ul
                  key="items"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
                  className="overflow-hidden flex flex-wrap gap-x-4 gap-y-1 text-sm text-ivory/85 mb-4"
                >
                  {s.items.map((it) => (
                    <li key={it} className="flex items-center gap-2">
                      <span className="h-1 w-1 rounded-full bg-gold" />
                      {it}
                    </li>
                  ))}
                </motion.ul>
              ) : (
                <motion.p
                  key="desc"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-sm text-ivory/80 leading-relaxed max-w-md mb-4"
                >
                  {s.description}
                </motion.p>
              )}
            </AnimatePresence>

            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => openWhatsApp(`Merhaba Hülya Suna Beauty, ${s.title} hakkında bilgi almak ve randevu oluşturmak istiyorum.`)}
                data-testid={`service-book-${s.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-ivory px-5 py-2.5 text-espresso text-sm font-medium transition-transform duration-300 hover:scale-[1.03] active:scale-95"
              >
                Bu hizmet için randevu al
                <ArrowUpRight size={16} />
              </button>
              <button
                onClick={() => setOpen((o) => !o)}
                data-testid={`service-details-${s.slug}`}
                className="link-underline text-sm text-ivory/80"
              >
                Detayları Gör
              </button>
            </div>
          </div>
        </div>
      </article>
    </FadeUp>
  );
}

export default function Services() {
  return (
    <section id="hizmetler" className="bg-ivory py-20 sm:py-28" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-14 lg:flex lg:items-end lg:justify-between gap-8">
          <div>
            <FadeUp>
              <p className="eyebrow text-gold mb-4">Hizmetler</p>
            </FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="font-serif text-espresso text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-xl">
                Size Özel <span className="italic">Güzellik Deneyimi</span>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.1}>
            <p className="mt-6 lg:mt-0 max-w-sm text-espresso/65 text-base leading-relaxed">
              Her uygulama; ihtiyacınıza, stilinize ve özel gününüze göre kişiye özel
              olarak ele alınır. Fiyat yerine, size en uygun planı birlikte belirliyoruz.
            </p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {SERVICES.map((s) => (
            <ServiceCard key={s.slug} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
